import { t } from "../lib/i18n";
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProperty, getPropertyValuation, savePropertyValuationResult, getSavedPropertyValuationResult } from '../api'; // Import the new API function
import Tooltip from './Tooltip';
import { showConfirm, showAlert } from '../utils/ModalService';



const parseValuationResponse = (data: any) => {
    if (data && data.valuation && typeof data.valuation === 'string') {
        try {
            let jsonString = data.valuation.trim();
            if (jsonString.startsWith('```json')) {
                jsonString = jsonString.replace(/^```json\s*|\s*```$/g, '');
            } else if (jsonString.startsWith('```')) {
                jsonString = jsonString.replace(/^```\s*|\s*```$/g, '');
            }
            return JSON.parse(jsonString);
        } catch (e) {
            console.error("Error parsing valuation string in frontend:", e);
        }
    }
    return data;
};

const Valuation: React.FC = () => {
    const { propertyId } = useParams<{ propertyId: string }>();
    const [valuation, setValuation] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [valuationMethod, setValuationMethod] = useState<string>("comparable_sales"); // Default method
    const [savedValuation, setSavedValuation] = useState<any>(null); // New state for saved valuation
    const [propertyData, setPropertyData] = useState<any>(null);

    const fetchValuation = async () => {
        if (!propertyId) return;
        setLoading(true);
        setError(null);

        try {
            const response = await getPropertyValuation(propertyId, valuationMethod);
            console.log("Valuation API Response:", response);
            const parsed = parseValuationResponse(response);
            setValuation(parsed);
        } catch (err: any) {
            if (err.response && err.response.data && err.response.data.detail) {
                const detail = err.response.data.detail;
                setError(typeof detail === 'object' ? JSON.stringify(detail) : detail);
            } else {
                setError('An unexpected error occurred while fetching the property valuation.');
            }
            console.error(err);
        }

        setLoading(false);
    };

    const handleSaveValuation = async () => {
        if (!valuation || !propertyId) return;
        try {
            const inputParameters = { propertyId: propertyId, valuationMethod: valuationMethod }; // Define input parameters
            await savePropertyValuationResult(propertyId, {
                property_id: propertyId,
                input_parameters: inputParameters,
                generated_result: valuation
            });
            setSavedValuation(valuation); // Update savedValuation state
            showAlert('Property valuation saved successfully!');
        } catch (err) {
            console.error('Error saving property valuation:', err);
            showAlert('Failed to save property valuation.');
        }
    };

    const handleGetValuation = (e: React.FormEvent) => {
        console.log("handleGetValuation called at: ", new Date().toLocaleTimeString());
        e.preventDefault();
        fetchValuation();
    };

    useEffect(() => {
        const loadSavedValuation = async () => {
            if (!propertyId) return;
            setLoading(true);
            try {
                const saved = await getSavedPropertyValuationResult(propertyId);
                if (saved) {
                    const parsed = parseValuationResponse(saved);
                    setSavedValuation(parsed);
                    setValuation(parsed); // Also set valuation to the saved one for display
                }
            } catch (err) {
                console.error('Error loading saved property valuation:', err);
            }
            finally {
                setLoading(false);
            }
        };
        loadSavedValuation();
    }, [propertyId]);

    useEffect(() => {
        const fetchPropertyDetails = async () => {
            if (!propertyId) return;
            try {
                const data = await getProperty(propertyId);
                setPropertyData(data);
            } catch (err) {
                console.error('Error fetching property details in valuation:', err);
            }
        };
        fetchPropertyDetails();
    }, [propertyId]);

    const displayValuation = savedValuation || valuation;

    return (
        <div className="container mx-auto p-4">
            <Link to="/properties" className="text-primary hover:underline mb-4 inline-block">{t("valuation.k1")}</Link>
                        <h1 className="text-2xl font-bold mb-4 flex items-center">
                            {t("valuation.k2")} <Tooltip text="Property Valuation estimates the current market value of a property using various methodologies such as Sales Comparison, Income Approach, and Cost Approach. It provides a comprehensive breakdown of the valuation process and key factors influencing the property's worth. This Property Valuation is generated by an AI model based on available data and market conditions. It provides estimates and insights and should not be considered definitive financial or investment advice. Always consult with a qualified professional before making any investment decisions." />
                        </h1>
            <form onSubmit={handleGetValuation} className="mb-4">
                <div className="flex mb-4">
                    <select
                        value={valuationMethod}
                        onChange={(e) => setValuationMethod(e.target.value)}
                        className="border border-border-primary p-2 rounded w-full"
                    >
                        <option value="comparable_sales">{t("valuation.k3")}</option>
                        <option value="income_approach">{t("valuation.k4")}</option>
                        <option value="discounted_cash_flow">{t("valuation.k5")}</option>
                        <option value="cost_approach">{t("valuation.k6")}</option>
                    </select>
                    <button type="submit" className="bg-primary text-white p-2 rounded hover:bg-primary-dark ml-2" disabled={loading}>
                        {loading ? t("valuation.k7") : t("valuation.k8")}
                    </button>
                    {displayValuation && !loading && (
                        <button type="button" onClick={handleSaveValuation} className="ml-2 bg-primary text-white p-2 rounded">
                            {t("valuation.k9")} </button>
                    )}
                </div>
            </form>

            {loading && <p>{t("valuation.k10")}</p>}
            {error && <p className="text-danger">{error}</p>}

            {displayValuation && (
                <div className="border border-border-primary p-4 rounded">
                    {console.log("displayValuation:", displayValuation)}
                    <h2 className="text-xl font-bold">{t("valuation.k11")} {propertyData?.address || displayValuation.property_custom_id || displayValuation.property_id}</h2>
                    
                    {displayValuation.valuation_summary && (
                        <div className="mt-4">
                            <h3 className="text-lg font-semibold">{t("valuation.k12")}</h3>
                            {displayValuation.valuation_summary.final_recommended_value && (
                                <p>
                                    <strong>{t("valuation.k13")}</strong>{' '}
                                    {typeof displayValuation.valuation_summary.final_recommended_value === 'number'
                                        ? displayValuation.valuation_summary.final_recommended_value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                                        : typeof displayValuation.valuation_summary.final_recommended_value === 'string' && !isNaN(Number(displayValuation.valuation_summary.final_recommended_value.replace(/[^0-9.]/g, '')))
                                            ? Number(displayValuation.valuation_summary.final_recommended_value.replace(/[^0-9.]/g, '')).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                                            : String(displayValuation.valuation_summary.final_recommended_value)
                                    }
                                </p>
                            )}
                            {displayValuation.valuation_summary.valuation_date && (
                                <p><strong>{t("valuation.k14")}</strong> {displayValuation.valuation_summary.valuation_date}</p>
                            )}
                            {displayValuation.valuation_summary.valuation_method && (
                                <p><strong>{t("valuation.k15")}</strong> {displayValuation.valuation_summary.valuation_method}</p>
                            )}
                            {displayValuation.valuation_summary.assumptions && (
                                <p><strong>{t("valuation.k16")}</strong> {displayValuation.valuation_summary.assumptions}</p>
                            )}
                            {displayValuation.valuation_summary.disclaimer && (
                                <p><strong>{t("valuation.k17")}</strong> {displayValuation.valuation_summary.disclaimer}</p>
                            )}
                        </div>
                    )}

                    {displayValuation.valuation_details && (
                        <div className="mt-4">
                            <h3 className="text-lg font-semibold">{t("valuation.k18")}</h3>
                            {Object.keys(displayValuation.valuation_details).map((categoryKey: string) => {
                                const category = displayValuation.valuation_details[categoryKey];
                                return (
                                    <div key={categoryKey} className="mt-3 p-3 border border-border-primary rounded bg-gray-50">
                                        <h4 className="text-md font-bold mb-2">{categoryKey.replace(/_/g, ' ').toUpperCase()}</h4>
                                        {typeof category === 'object' && category !== null ? (
                                            Object.keys(category).map((detailKey: string) => {
                                                const detailValue = category[detailKey];

                                                const formatValue = (key: string, value: any) => {
                                                    if (typeof value === 'number') {
                                                        if (key.includes('price') || key.includes('value') || key.includes('income') || key.includes('cost') || key.includes('expenses')) {
                                                            return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
                                                        } else if (key.includes('rate') || key.includes('percentage')) {
                                                            return `${(value * 100).toFixed(2)}%`;
                                                        }
                                                    }
                                                    return String(value);
                                                };

                                                if (typeof detailValue === 'object' && detailValue !== null) {
                                                    return (
                                                        <div key={detailKey} className="ml-4 mt-2">
                                                            <p><strong>{detailKey.replace(/_/g, ' ').toUpperCase()}:</strong></p>
                                                            {Array.isArray(detailValue) ? (
                                                                detailValue.map((item: any, index: number) => (
                                                                    <div key={index} className="ml-4 mb-2 p-2 border-l-2 border-border-primary">
                                                                        {typeof item === 'object' && item !== null ? (
                                                                            Object.keys(item).map((itemKey: string) => {
                                                                                const nestedItemValue = item[itemKey];
                                                                                if (typeof nestedItemValue === 'object' && nestedItemValue !== null) {
                                                                                    return (
                                                                                        <div key={itemKey} className="ml-4">
                                                                                            <p><strong>{itemKey.replace(/_/g, ' ').toUpperCase()}:</strong></p>
                                                                                            {Object.keys(nestedItemValue).map((subItemKey: string) => (
                                                                                                <p key={subItemKey} className="ml-4"><strong>{subItemKey.replace(/_/g, ' ').toUpperCase()}:</strong> {formatValue(subItemKey, nestedItemValue[subItemKey])}</p>
                                                                                            ))}
                                                                                        </div>
                                                                                    );
                                                                                } else {
                                                                                    return <p key={itemKey}><strong>{itemKey.replace(/_/g, ' ').toUpperCase()}:</strong> {formatValue(itemKey, nestedItemValue)}</p>;
                                                                                }
                                                                            })
                                                                        ) : (
                                                                            <p>{formatValue(detailKey, item)}</p>
                                                                        )}
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                Object.keys(detailValue).map((nestedKey: string) => (
                                                                    <p key={nestedKey} className="ml-4"><strong>{nestedKey.replace(/_/g, ' ').toUpperCase()}:</strong> {formatValue(nestedKey, detailValue[nestedKey])}</p>
                                                                ))
                                                            )}
                                                        </div>
                                                    );
                                                } else {
                                                    return <p key={detailKey}><strong>{detailKey.replace(/_/g, ' ').toUpperCase()}:</strong> {formatValue(detailKey, detailValue)}</p>;
                                                }
                                            })
                                        ) : (
                                            <p>{String(category)}</p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Valuation;